import { selectEnableLocationSmart } from '~/components/brand/ducks/selectors';

import { ACTION_PREFIX } from '../../sportsbook-constants';
import createReducer from '../../../../../utils/createReducer';
import { RootState, ThunkAction } from '../../../../../store/types';
import selectSportsbookState from '../../sportsbook-state-selector';
import { LocationVerificationStatus, startLocationSmart, stopLocationSmart } from './sportsbook-location-smart';

const LOCAL_STORAGE_KEY = 'sportsbookIsLocationSharingAllowed';

const LOCATION_SHARING_ALLOWED = `${ACTION_PREFIX}LOCATION_VERIFICATION/LOCATION_SHARING_ALLOWED` as const;
const LOCATION_VERIFICATION_UPDATED = `${ACTION_PREFIX}LOCATION_VERIFICATION/LOCATION_VERIFICATION_UPDATED` as const;
const DISPOSED = `${ACTION_PREFIX}LOCATION_VERIFICATION/DISPOSED` as const;

type State = {
	isLocationSharingAllowed: boolean;
	status: LocationVerificationStatus;
};

type LocationSharingAllowedAction = {
	type: typeof LOCATION_SHARING_ALLOWED;
};

type LocationVerificationUpdatedAction = {
	type: typeof LOCATION_VERIFICATION_UPDATED;
	payload: { status: LocationVerificationStatus };
};

type DisposedAction = {
	type: typeof DISPOSED;
};

// Selectors
export const selectState = (state: RootState) => selectSportsbookState(state).locationVerification;
export const selectIsLocationSharingAllowed = (state: RootState) => selectState(state).isLocationSharingAllowed;
export const selectLocationVerificationStatus = (state: RootState) => selectState(state).status;

export const selectIsLocationVerificationEnabled = (state: RootState) => selectEnableLocationSmart(state);

export const selectIsLocationNotShared = (state: RootState) =>
	selectIsLocationVerificationEnabled(state) && !selectIsLocationSharingAllowed(state);

export const selectIsLocationPending = (state: RootState) =>
	selectIsLocationVerificationEnabled(state) &&
	selectIsLocationSharingAllowed(state) &&
	selectLocationVerificationStatus(state) === LocationVerificationStatus.PENDING;

export const selectIsLocationAllowed = (state: RootState) =>
	!selectIsLocationVerificationEnabled(state) ||
	(selectIsLocationSharingAllowed(state) &&
		selectLocationVerificationStatus(state) === LocationVerificationStatus.ALLOWED);

export const selectIsLocationDenied = (state: RootState) =>
	selectIsLocationVerificationEnabled(state) &&
	selectIsLocationSharingAllowed(state) &&
	selectLocationVerificationStatus(state) === LocationVerificationStatus.DENIED;

export const selectIsLocationError = (state: RootState) =>
	selectIsLocationVerificationEnabled(state) &&
	selectIsLocationSharingAllowed(state) &&
	selectLocationVerificationStatus(state) === LocationVerificationStatus.ERROR;

// Action creators
export const init = (): ThunkAction<LocationVerificationUpdatedAction> => (dispatch, getState) => {
	const state = getState();
	if (selectIsLocationVerificationEnabled(state) && selectIsLocationSharingAllowed(state)) {
		startLocationSmart(status =>
			dispatch({
				type: LOCATION_VERIFICATION_UPDATED,
				payload: { status }
			})
		);
	}
};

export const allowLocationSharing =
	(): ThunkAction<LocationSharingAllowedAction | LocationVerificationUpdatedAction> => (dispatch, getState) => {
		const state = getState();
		if (!selectIsLocationVerificationEnabled(state) || selectIsLocationSharingAllowed(state)) {
			return;
		}

		dispatch({ type: LOCATION_SHARING_ALLOWED });

		startLocationSmart(status =>
			dispatch({
				type: LOCATION_VERIFICATION_UPDATED,
				payload: { status }
			})
		);

		if (window?.localStorage) {
			window.localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
		}
	};

export const dispose = (): ThunkAction<DisposedAction> => dispatch => {
	stopLocationSmart();
	dispatch({ type: DISPOSED });
};

// Reducer
const initialState: State = {
	isLocationSharingAllowed: !!window?.localStorage?.getItem(LOCAL_STORAGE_KEY),
	status: LocationVerificationStatus.PENDING
};

const handleLocationSharingAllowed = (state: State) => ({
	...state,
	isLocationSharingAllowed: true
});

const handleLocationVerificationUpdated = (
	state: State,
	{ payload: { status } }: LocationVerificationUpdatedAction
) => ({
	...state,
	status
});

const handleDisposed = (state: State) => ({
	...state,
	status: LocationVerificationStatus.PENDING
});

export default createReducer(initialState, {
	[LOCATION_SHARING_ALLOWED]: handleLocationSharingAllowed,
	[LOCATION_VERIFICATION_UPDATED]: handleLocationVerificationUpdated,
	[DISPOSED]: handleDisposed
});
